// @ts-nocheck
import 'src/utils/polyfills';

import React from 'react';
import {createRoot, hydrateRoot} from 'react-dom/client';
import {RouterProvider} from 'src/rerouter/index';
import * as sentry from 'sentry';

import {browserHistory} from 'src/reroutes/browser-history';
import {bootstrapStores} from 'src/client/bootstrap';
import logger from 'src/utils/logger';
import App from 'src/components/referrer-portal/app';
import ErrorBoundary from 'src/components/500/boundary';
import {PartialError as PartialErrorComponent} from 'src/components/206/index';
import {initializeFlags} from 'src/actions/index';
import {
  showGenericRuntimeError,
  showUnhandledApiError,
} from 'src/action-creators/modal';
import {showPartialErrorToast} from 'src/action-creators/toasts';
import {
  ApiError,
  PartialError,
  captureApiError,
  wranglePossibleApiError,
} from 'src/utils/errors';
import {isDevOrStage} from 'src/utils/env';


const {reduxStore, store, env} = bootstrapStores(browserHistory);

const appElement = document.getElementById('app');
const root =
  env.query.render_mode === 'client'
    ? createRoot(appElement)
    : hydrateRoot(appElement);

if (reduxStore.getState().accounts.authedUserId !== null) {
  const uid = reduxStore.getState().accounts.authedUserId;
  const scope = sentry.getCurrentScope();
  scope.setUser({
    id: uid,
    username: reduxStore.getState().accounts.data[uid].name,
  });
  logger.updateContext({
    userid: uid,
  });
  // NOTE (vishal): we need this for walkme
  window.__senseUserId = uid;
}

const renderWith = () => {
  root?.render(
    <ErrorBoundary>
      <RouterProvider history={browserHistory}>
        <App reduxStore={reduxStore} senseStore={store} />
      </RouterProvider>
    </ErrorBoundary>,
  );
};

renderWith();

if (store.me.isAuthed()) {
  initializeFlags(store);

  // App Cues
  // NOTE (vishal): if we ever go back to ajax login, we'll need to add this to the login action
  const {Appcues} = window;
  if (Appcues) {
    Appcues.start();
    const {agent} = store.me.state;
    Appcues.identify(agent.id, {
      name: agent.name,
    });
  }
}

if (isDevOrStage({env})) {
  window.addEventListener('error', (...args) => {
    const {dispatch} = reduxStore;
    dispatch(showGenericRuntimeError(...args));
  });
}

// NOTE (vishal): Support for this in Chrome is highly dependent on obscure
// security conditions, and core-js never polyfills Promise in Chrome.
// Hopefully this will improve over time, but there is no guarentee.
// NOTE (vishal): When @babel/polyfill upgrades to core-js@3, we should be
// able to use the more elegant, addEventListener version of this.

const UNAUTHORIZED_STATUSES = [401, 403];

window.onunhandledrejection = (event: unknown) => {
  let {reason} = event;
  // NOTE (vishal): utils/api.js returns a weird legacy error object.
  reason = wranglePossibleApiError(reason);

  window.lastUnhandledError = reason;

  const isAuthed = reduxStore.getState()?.referrerPortal?.isAuthed;
  const isAuthorizedStatus =
    isAuthed && !UNAUTHORIZED_STATUSES.includes(reason.response.status);

  if (reason instanceof PartialError) {
    captureApiError(reason, null, {unhandled: true});
    reduxStore.dispatch(showPartialErrorToast(<PartialErrorComponent />));
  } else if (reason instanceof ApiError) {
    captureApiError(reason, null, {unhandled: true});
    if (isAuthorizedStatus) {
      reduxStore.dispatch(showUnhandledApiError(reason, sentry.lastEventId()));
    }
  } else {
    sentry.captureException(reason);
  }
};

// To ease live debugging
window.senseStore = store;
// NOTE(gab): Don't delete this unless you've verified via grepping the codebase that it's not used
window.__reduxStore = reduxStore;
window.logger = logger;
